import type { TicketingItem, TicketingTab } from "../../../types/tickets";

/** Anchor of the Event Setup section at the top of the overview page. */
export const EVENT_SETUP_ANCHOR = "event-setup";

/** The per-ticket guided flow. One ticket type at a time. */
export const STEPS = [
  { key: "details", label: "Details" },
  { key: "modifiers", label: "Modifiers" },
  { key: "placement", label: "Placement" },
  { key: "gates", label: "Gates" },
  { key: "review", label: "Review" },
] as const;

export type StepKey = (typeof STEPS)[number]["key"];

export const isStepKey = (s: unknown): s is StepKey =>
  typeof s === "string" && STEPS.some((x) => x.key === s);

/** Where a check sends the user: a flow step, or the Event Setup section on the overview. */
export type CheckTarget = StepKey | "event";

export const ACCESS_CONTROL_TYPE = "Access Control";
export const PUBLIC_SITE_BASE = "https://ticketing.roninpos.app/events/";

export const OVERVIEW_PATH = "/ticketing-setup";
export const ticketPath = (ticketId: number | "new", step: StepKey = "details") =>
  `${OVERVIEW_PATH}/ticket/${ticketId}/${step}`;

export interface SetupProcessor {
  id: number;
  processor: string | null;
  payment_processor: string | null;
  is_active: boolean;
  is_rfid?: boolean;
  config: Record<string, unknown> | null;
}

export interface SetupEvent {
  id: number;
  name: string;
  is_active: boolean;
  organization_id: number;
  /** Rich-text HTML shown on the public ticketing page (same column the Event page edits). */
  description: string | null;
  /** The event's POS processor. A new gate copies it, like the Location page's default. */
  payment_processor_id: number | null;
  location_zones: string[] | null;
  ticketing_slug: string | null;
  ticketing_logo: string | null;
  ticketing_background_url: string | null;
  ticketing_payment_processor_id: number | null;
  ticketing_service_fee_per_order: number | null;
  web_surcharge_percentage: number | null;
  ticketing_configurations: { show_remaining_cta?: boolean } | null;
  ticketing_payment_processor_config: SetupProcessor | null;
}

export interface SetupGate {
  id: number;
  name: string;
  location_type: string | null;
  zone: string | null;
  vendor_id: number | null;
  redeemable_tokens: string[] | null;
}

export interface SetupVendor {
  id: number;
  name: string;
}

export interface SetupData {
  events: { id: number; name: string }[];
  eventId: number | null;
  event: SetupEvent | null;
  processors: SetupProcessor[];
  tickets: TicketingItem[];
  tabs: TicketingTab[];
  gates: SetupGate[];
  vendors: SetupVendor[];
  categories: { reference_label: string; reference_value: string }[];
  organizationId: number | null;
}

/** A ticket "has sales" when its remaining balance dropped below the limit. Best signal without a sales query. */
export const ticketHasSales = (t: TicketingItem): boolean =>
  t.sales_limit != null && t.sales_limit_balance != null && Number(t.sales_limit_balance) < Number(t.sales_limit);

export const isPublicTicket = (t: TicketingItem): boolean =>
  !!t.is_active && !!t.available_public_sale && !t.cashless_top_up_item;
